import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import useVideoPlayer from "./useVideoPlayer";
import CategorySection from "./CategorySection";
import HeroVideo from "./HeroVideo";
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Form,
  InputGroup,
  ProgressBar,
  Modal,
} from "react-bootstrap";
import {
  IconPlayerPlay,
  IconSearch,
  IconClock,
  IconTrendingUp,
  IconX,
  IconCheck,
  IconExternalLink,
} from "@tabler/icons-react";
import ReactPlayer from "react-player";
import UserContext from "../../../contexts/UserContext";
import CoursesService from "./CoursesServices";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Courses.css";

const CoursesDashboard = () => {
  const { user, getUserPlan } = useContext(UserContext);
  const searchRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [categoriesWithVideos, setCategoriesWithVideos] = useState([]);
  const [nextVideos, setNextVideos] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterBy, setFilterBy] = useState("all");

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [heroVideo, setHeroVideo] = useState(null);
  const playerRef = useRef(null);

  const handleVideoCompleted = useCallback((videoId) => {
    setCategoriesWithVideos((prev) =>
      prev.map((cat) => ({
        ...cat,
        completed_count:
          cat.completed_count +
          (cat.videos.some((v) => v.id === videoId) ? 1 : 0),
        videos: cat.videos.map((v) =>
          v.id === videoId
            ? { ...v, is_completed: true, progress_percent: 100 }
            : v
        ),
      }))
    );
    setAllVideos((prev) =>
      prev.map((v) =>
        v.id === videoId
          ? { ...v, is_completed: true, progress_percent: 100 }
          : v
      )
    );
    setNextVideos((prev) => prev.filter((v) => v.id !== videoId));
  }, []);

  const videoPlayer = useVideoPlayer(selectedVideo, {
    onComplete: handleVideoCompleted,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (videoPlayer?.disconnectPlayer) videoPlayer.disconnectPlayer();
    if (playerRef.current && playing) {
      try { playerRef.current.getInternalPlayer()?.pause?.(); } catch (e) {}
    }
    setPlaying(false);
    setTimeout(() => {
      setShowVideoModal(false);
      setSelectedVideo(null);
    }, 100);
  }, [playing, videoPlayer]);

  const currentPlan = getUserPlan();
  const hasAccess = user && currentPlan > 1

  useEffect(() => {
    if (hasAccess) loadCourseData();
  }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nextVideos.length > 0) {
      const videoWithProgress = nextVideos.find(
        (v) => v.progress_percent > 0 && v.progress_percent < 100
      );
      setHeroVideo(videoWithProgress || nextVideos[0]);
    }
  }, [nextVideos]);

  useEffect(() => {
    const currentPlayer = playerRef.current;
    return () => {
      if (currentPlayer && playing) {
        try { currentPlayer.getInternalPlayer()?.pause?.(); } catch (e) {}
      }
    };
  }, [playing]);

  useEffect(() => {
    if (!showVideoModal) return;
    const handleEscKey = (event) => {
      if (event.key === "Escape") handleCloseModal();
    };
    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [showVideoModal, handleCloseModal]);

  const loadCourseData = useCallback(async (retryCount = 0) => {
    try {
      setIsLoading(true);
      setError(null);
      const dashboardResult = await CoursesService.getGlobalDashboard();
      if (dashboardResult.success) {
        const categories = Array.isArray(dashboardResult.data?.categories)
          ? dashboardResult.data.categories : [];
        const nextVids = Array.isArray(dashboardResult.data?.nextVideos)
          ? dashboardResult.data.nextVideos : [];
        const recent = Array.isArray(dashboardResult.data?.recentActivity)
          ? dashboardResult.data.recentActivity : [];
        setCategoriesWithVideos(categories);
        setAllVideos(categories.flatMap((c) => c.videos || []));
        setNextVideos(nextVids);
        setRecentActivity(recent);
      } else {
        const errorMsg = dashboardResult.error || "Erro desconhecido ao carregar dashboard";
        if (retryCount < 2 && (errorMsg.includes("rede") || errorMsg.includes("conexão"))) {
          setTimeout(() => loadCourseData(retryCount + 1), 1000 * (retryCount + 1));
          return;
        }
        setError(errorMsg);
      }
    } catch (error) {
      const errorMsg = error.name === "NetworkError" || !navigator.onLine
        ? "Erro de conexão. Verifique sua internet e tente novamente."
        : "Erro interno do sistema. Tente novamente em alguns instantes.";
      if (retryCount < 2 && (error.name === "NetworkError" || !navigator.onLine)) {
        setTimeout(() => loadCourseData(retryCount + 1), 2000 * (retryCount + 1));
        return;
      }
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleVideoPlay = (video) => {
    setPlaying(false);
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  const handlePlayerError = () => setPlaying(false);

  const handleActivityClick = (activity) => {
    handleVideoPlay({
      id: activity.video_id,
      title: activity.video_title,
      category_name: activity.category_name,
      video_type: activity.video_type || "internal",
      video_url: activity.video_url,
      s3_url: activity.s3_url,
      external_url: activity.external_url,
      thumbnail_url: activity.thumbnail_url,
      thumbnail_final_url: activity.thumbnail_final_url,
      duration_display: activity.duration_display,
      progress_percent: activity.progress_percent,
      last_position_seconds: activity.last_position_seconds,
    });
  };

  const getFilteredVideos = () => {
    let videos = allVideos;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      videos = videos.filter(
        (v) =>
          v.title.toLowerCase().includes(term) ||
          (v.description || "").toLowerCase().includes(term) ||
          (v.category_name || "").toLowerCase().includes(term)
      );
    }
    if (filterBy === "in-progress") {
      videos = videos.filter((v) => v.progress_percent > 0 && v.progress_percent < 100);
    } else if (filterBy === "completed") {
      videos = videos.filter((v) => v.is_completed || v.progress_percent === 100);
    }
    return videos;
  };

  const filteredVideos = getFilteredVideos();

  // No access
  if (!hasAccess) {
    return (
      <div className="crs-app">
        <div className="crs-error crs-no-access">
          <Container>
            <Alert className="text-center border-0 bg-warning bg-opacity-10">
              <div className="mb-3">
                <IconTrendingUp size={40} className="text-warning" />
              </div>
              <h4 className="text-warning mb-3">Acesso Premium Necessario</h4>
              <p className="text-muted mb-4">
                Os cursos profissionais estao disponiveis apenas para usuarios Premium.
              </p>
              <Button variant="primary" className="px-4 py-2">
                <IconTrendingUp size={18} className="me-2" />
                Fazer Upgrade
              </Button>
            </Alert>
          </Container>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <div className="crs-app">
        <div className="crs-loading">
          <Spinner animation="border" />
          <p>Carregando sua biblioteca de cursos...</p>
          <small>Preparando o melhor conteudo para voce</small>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="crs-app">
        <div className="crs-error">
          <Container>
            <Alert className="text-center border-0">
              <div className="mb-3">
                <IconX size={40} className="text-danger" />
              </div>
              <h4 className="text-danger mb-3">Algo deu errado</h4>
              <p className="text-muted mb-4">{error}</p>
              <Button variant="outline-primary" onClick={loadCourseData}>
                Tentar Novamente
              </Button>
            </Alert>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="crs-app">
      {/* Header */}
      <header className="crs-header">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs={12} lg={6} className="mb-3 mb-lg-0">
              <div className="crs-header__brand">
                <h1 className="crs-header__title">
                  <IconTrendingUp
                    size={isMobile ? 22 : 28}
                    className="crs-header__title-icon"
                  />
                  Aprendizado
                </h1>
                <p className="crs-header__subtitle">
                  Aprenda a criar, vender e divulgar seus agentes!
                </p>
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className="crs-header__search-wrap">
                <InputGroup className="crs-header__search">
                  <Button
                    variant="link"
                    className="crs-header__search-btn"
                    onClick={() => searchRef.current?.focus()}
                  >
                    <IconSearch size={18} />
                  </Button>
                  <Form.Control
                    ref={searchRef}
                    type="text"
                    placeholder="Buscar aulas, categorias..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="crs-header__search-input"
                  />
                </InputGroup>
                <div className="crs-header__filters">
                  {[
                    { key: "all", label: "Todos" },
                    { key: "in-progress", label: "Em andamento" },
                    { key: "completed", label: "Concluidos" },
                  ].map((f) => (
                    <button
                      key={f.key}
                      className={`crs-filter ${filterBy === f.key ? "crs-filter--active" : ""}`}
                      onClick={() => setFilterBy(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </header>

      {/* Hero */}
      {!searchTerm && heroVideo && (
        <HeroVideo video={heroVideo} onPlay={handleVideoPlay} />
      )}

      {/* Main */}
      <main className="crs-main">
        <Container fluid>
          {/* Continue assistindo */}
          {nextVideos.length > 0 &&
            !searchTerm &&
            nextVideos.some((v) => v.progress_percent > 0) && (
              <CategorySection
                category={{
                  name: "Continue assistindo",
                  id: "continue",
                  completion_percentage: 0,
                  is_completed: false,
                  completed_count: nextVideos.filter((v) => v.is_completed).length,
                  videos_count: nextVideos.length,
                  videos: nextVideos.slice(0, 10),
                }}
                onVideoPlay={handleVideoPlay}
                isMobile={isMobile}
              />
            )}

          {/* Categorias */}
          {!searchTerm &&
            categoriesWithVideos.length > 0 &&
            categoriesWithVideos
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((category) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  onVideoPlay={handleVideoPlay}
                  isMobile={isMobile}
                />
              ))}

          {/* Search/Filter results */}
          {(searchTerm || filterBy !== "all") && (
            <section className="crs-search">
              <Container>
                <div className="crs-search__header">
                  <h2 className="crs-search__title">
                    {searchTerm
                      ? `Resultados para "${searchTerm}"`
                      : `Filtrando por: ${{ "in-progress": "Em andamento", completed: "Concluidos" }[filterBy]}`}
                  </h2>
                  <p className="crs-search__count">
                    {filteredVideos.length}{" "}
                    {filteredVideos.length === 1 ? "resultado" : "resultados"}
                  </p>
                </div>

                {filteredVideos.length > 0 ? (
                  <div className="crs-grid">
                    {filteredVideos.map((video) => (
                      <div key={video.id} className="crs-grid__item">
                        <div
                          className="crs-card crs-card--search"
                          onClick={() => handleVideoPlay(video)}
                        >
                          <div className="crs-card__thumb">
                            <img
                              src={video.thumbnail_final_url || video.thumbnail_url || "/placeholder-video.jpg"}
                              alt={video.title}
                              loading="lazy"
                              onError={(e) => { e.target.src = "/placeholder-video.jpg"; }}
                            />
                            <div className="crs-card__overlay">
                              <div className="crs-card__play">
                                <IconPlayerPlay size={20} />
                              </div>
                            </div>
                            {video.progress_percent > 0 && (
                              <div className="crs-card__progress">
                                <div className="crs-card__progress-fill" style={{ width: `${video.progress_percent}%` }} />
                              </div>
                            )}
                          </div>
                          <div className="crs-card__info">
                            <h4 className="crs-card__title">{video.title}</h4>
                            <p className="crs-card--search__desc">
                              {video.description || video.category_name}
                            </p>
                            <div className="crs-card__meta">
                              <span className="crs-card__duration">
                                <IconClock size={10} />
                                {video.duration_display || "--"}
                              </span>
                              {video.is_completed && (
                                <IconCheck size={14} className="crs-card__check" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="crs-empty">
                    <div className="crs-empty__icon">
                      <IconSearch size={48} />
                    </div>
                    <h3>Nenhum resultado encontrado</h3>
                    <p>
                      {searchTerm
                        ? "Tente pesquisar por outros termos ou explore nossas categorias."
                        : "Nenhum curso encontrado para este filtro."}
                    </p>
                    {searchTerm && (
                      <Button variant="outline-primary" onClick={() => setSearchTerm("")} className="mt-3">
                        Limpar busca
                      </Button>
                    )}
                  </div>
                )}
              </Container>
            </section>
          )}

          {/* Quick Access Mobile */}
          {recentActivity.length > 0 && !searchTerm && isMobile && (
            <section className="crs-quick mb-4">
              <Container>
                <h2 className="crs-quick__title mb-3">
                  <IconClock size={18} className="me-2" />
                  Continue de onde parou
                </h2>
                <div className="crs-quick__list">
                  {recentActivity.slice(0, 3).map((activity, index) => (
                    <div
                      key={index}
                      className="crs-quick__item"
                      onClick={() => handleActivityClick(activity)}
                    >
                      <div className="crs-quick__thumb">
                        <img
                          src={activity.thumbnail_final_url || activity.thumbnail_url || "/placeholder-video.jpg"}
                          alt={activity.video_title}
                          onError={(e) => { e.target.src = "/placeholder-video.jpg"; }}
                        />
                        <div className="crs-quick__play-icon">
                          <IconPlayerPlay size={12} />
                        </div>
                      </div>
                      <div className="crs-quick__info">
                        <h4>{activity.video_title}</h4>
                        <p>{activity.category_name}</p>
                        {!activity.completed && (
                          <ProgressBar now={activity.progress_percent} className="crs-quick__mini-progress" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Container>
            </section>
          )}
        </Container>
      </main>

      {/* Video Modal (Dark Immersive) */}
      <Modal
        show={showVideoModal}
        onHide={handleCloseModal}
        size="xl"
        fullscreen
        className="crs-modal"
        backdrop="static"
        keyboard={true}
      >
        <div className="crs-modal__header">
          <Button
            variant="link"
            onClick={handleCloseModal}
            className="crs-modal__close"
            aria-label="Fechar"
          >
            <IconX size={22} />
          </Button>
        </div>

        <div className="crs-modal__body">
          {selectedVideo && (
            <>
              <div className="crs-modal__player">
                {videoPlayer.loading && (
                  <div className="crs-player-loading">
                    <Spinner animation="border" />
                    <p>Carregando video...</p>
                  </div>
                )}

                {videoPlayer.hasError && (
                  <div className="crs-player-error">
                    <IconX size={40} className="text-danger" />
                    <h4>Erro ao carregar video</h4>
                    <p>{videoPlayer.error}</p>
                  </div>
                )}

                {videoPlayer.isInternal &&
                  videoPlayer.uploadStatus?.status === "processing" && (
                    <div className="crs-player-processing">
                      <Spinner animation="border" />
                      <h4>Processando video...</h4>
                      <p>O video esta sendo processado. Isso pode levar alguns minutos.</p>
                      {videoPlayer.uploadStatus.progress && (
                        <ProgressBar now={videoPlayer.uploadStatus.progress} label={`${videoPlayer.uploadStatus.progress}%`} style={{ width: "100%", maxWidth: 300 }} />
                      )}
                    </div>
                  )}

                {selectedVideo &&
                  videoPlayer.isVideoAvailable &&
                  !videoPlayer.loading &&
                  !videoPlayer.hasError && (
                    <>
                      <ReactPlayer
                        ref={playerRef}
                        src={videoPlayer.getStreamUrl(selectedVideo)}
                        playing={playing}
                        controls={true}
                        width="100%"
                        height="100%"
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onError={handlePlayerError}
                        onReady={() => {
                          if (playerRef.current) videoPlayer.connectPlayer(playerRef.current);
                        }}
                        config={{
                          file: { attributes: { preload: "metadata", crossOrigin: "anonymous" } },
                          vimeo: { playerOptions: { controls: true, responsive: true } },
                        }}
                        fallback={
                          <div className="crs-player-loading">
                            <Spinner animation="border" />
                            <p>Carregando player...</p>
                          </div>
                        }
                      />

                      {!playing && (
                        <div className="crs-modal__play-overlay" onClick={() => setPlaying(true)}>
                          <div className="crs-modal__play-btn">
                            <IconPlayerPlay size={36} />
                          </div>
                          <p className="crs-modal__play-hint">Clique para reproduzir</p>
                        </div>
                      )}
                    </>
                  )}
              </div>

              <div className="crs-modal__info">
                <Container>
                  <Row>
                    <Col lg={8}>
                      <h1 className="crs-modal__video-title">{selectedVideo.title}</h1>
                      <div className="crs-modal__video-meta">
                        <span className="crs-modal__category-badge">
                          {selectedVideo.category_name}
                        </span>
                        <span className="crs-modal__duration">
                          <IconClock size={14} />
                          {selectedVideo.duration_display}
                        </span>
                        {selectedVideo.is_completed && (
                          <span className="crs-modal__completed-badge">
                            <IconCheck size={14} />
                            Concluido
                          </span>
                        )}
                      </div>
                      {selectedVideo.description && (
                        <p className="crs-modal__desc mb-4">
                          {selectedVideo.description}
                        </p>
                      )}
                      {(selectedVideo.progress_percent > 0 || videoPlayer.currentProgress > 0) && (
                        <div className="crs-modal__progress-wrap">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <small style={{ color: "rgba(255,255,255,0.6)" }}>Seu progresso</small>
                            <small style={{ color: "rgba(255,255,255,0.6)" }}>
                              {Math.round(videoPlayer.currentProgress || selectedVideo.progress_percent || 0)}%
                            </small>
                          </div>
                          <ProgressBar
                            now={videoPlayer.currentProgress || selectedVideo.progress_percent || 0}
                            className="crs-modal__progress-bar"
                          />
                          {videoPlayer.isVideoStarted && (
                            <div className="crs-modal__progress-details">
                              {videoPlayer.isCompleted ? (
                                <span style={{ color: "var(--success)" }}>Video completo!</span>
                              ) : (
                                <>
                                  Tempo assistido: {Math.floor(videoPlayer.totalWatchTime / 60)}m {videoPlayer.totalWatchTime % 60}s
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Materiais Auxiliares */}
                      {selectedVideo.links && selectedVideo.links.length > 0 && (
                        <div className="crs-modal__links">
                          <h6 className="crs-modal__links-title">Materiais da aula</h6>
                          <div className="crs-modal__links-list">
                            {selectedVideo.links.map((link) => (
                              <a
                                key={link.id}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="crs-modal__link-item"
                              >
                                <div className="crs-modal__link-icon">
                                  <IconExternalLink size={16} />
                                </div>
                                <div className="crs-modal__link-info">
                                  <span className="crs-modal__link-name">{link.title}</span>
                                  {link.description && (
                                    <span className="crs-modal__link-desc">{link.description}</span>
                                  )}
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </Col>
                    <Col lg={4}>
                      <div className="crs-modal__sidebar">
                        <h6>Outros da categoria</h6>
                        <p>Explore mais videos em {selectedVideo.category_name}</p>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CoursesDashboard;
